import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { PageParameters } from '../lib/api/types';
import { CellStateEnum } from '../lib/types';
import { entryToUuidForm } from '../lib/utils';
import { MediaUploadType } from '../modules/assets/types';
import { getFileUploaded } from '../modules/assets/utils';
import { createEntry, updateEntry } from '../modules/entries/entries.repository';
import { Entry, EntryContent, EntryValueType } from '../modules/entries/types';
import { getTable, getTableEntries } from '../modules/tables/tables.repository';
import { Table } from '../modules/tables/types';
import { CellLocation, FieldFilter, RowState, SortOrders, TableConfigurationState } from './types';
import {
  getBaseCellState,
  getCellsBaseState,
  initTableState,
  isInvalidRow,
  isInvalidValue,
  getNextSortingOrder,
  processFilters,
} from './utils';

interface ITableStore {
  table?: Table;
  tableState: RowState[];
  tableConfigurationState?: Record<string, TableConfigurationState>;
  selectedRow?: number;
  hoveredRow?: number;
  isModalOpen?: boolean;
}

class TableStore implements ITableStore {
  @observable table?: Table;

  @observable tableTotals: { totalItems: number; totalPages: number } = {
    totalItems: 0, 
    totalPages: 0,
  };

  @observable tableState: RowState[] = [];

  @observable tableConfigurationState?: Record<string, TableConfigurationState>;

  @observable selectedRow?: number;

  @observable hoveredRow?: number;

  @observable isModalOpen = false;

  constructor() {
    makeObservable(this);
  }

  @computed
  get referenceFieldNames() {
    if (this.table) {
      const { schema } = this.table;
      const refFields = Object.fromEntries(Object.entries(schema).filter(([, field]) => field.reference));
      return Object.keys(refFields);
    }
    return [];
  }

  @action
  async fetchTable(databaseSlug: string, tableSlug: string) {
    const response = await getTable(databaseSlug, tableSlug);
    runInAction(() => {
      this.table = response;
      if (!this.tableConfigurationState?.[tableSlug]) {
        this.tableConfigurationState = {
          ...(this.tableConfigurationState || {}),
          [response.slug]: { filters: {} },
        };
      }
    });
  }

  @action
  async fetchEntries(
    databaseSlug: string,
    tableSlug: string,
    pageParameters: PageParameters,
    query?: string,
    sorting?: [string, SortOrders],
    filters?: Record<string, string[]>,
  ) {
    const processedFilters = processFilters(filters);
    const response = await getTableEntries(databaseSlug, tableSlug, pageParameters, query, sorting, processedFilters);
    runInAction(() => {
      this.tableTotals = {
        totalItems: response.totalItems || 0,
        totalPages: response.totalPages || 1,
      };
      this.tableState = initTableState(response, this.table);
    });
  }

  @action
  flipSorting(tableSlug: string, sortingKey?: string) {
    const tableConfigurationState = this.tableConfigurationState?.[tableSlug];
    if (tableConfigurationState) {
      const sortingOrder = getNextSortingOrder(sortingKey, tableConfigurationState.sortOrder, tableConfigurationState.sortBy);
      tableConfigurationState.sortOrder = sortingOrder;
      tableConfigurationState.sortBy = sortingKey;
    }
  }

  @action
  toggleFilter(tableSlug: string, filterKey: string, fieldFilter: FieldFilter) {
    const tableConfigurationState = this.tableConfigurationState?.[tableSlug];
    if (tableConfigurationState) {
      const columnFilters = tableConfigurationState.filters[filterKey] || [];
      const filterValueIndex = columnFilters.findIndex(
        (columnFilter) => columnFilter.filterValue === fieldFilter.filterValue,
      );
      if (filterValueIndex === -1) {
        columnFilters.push(fieldFilter);
      } else {
        columnFilters.splice(filterValueIndex, 1);
      }
      tableConfigurationState.filters[filterKey] = columnFilters;
    }
  }

  @action clearSortingAndFilters(tableSlug?: string) {
    if (this.tableConfigurationState && tableSlug) {
      this.tableConfigurationState = { [tableSlug]: { filters: {} } };
    }
  }

  @action
  removeSelectedEntry() {
    if (this.selectedRow !== undefined) {
      this.tableState.splice(this.selectedRow, 1);
      // no row should be selected after
      this.selectedRow = undefined;
    }
  }

  @action
  async addEntry(
    databaseSlug: string,
    tableSlug: string,
  ) {
    if (this.table) {
      const { content, invalid, ...other } = await createEntry(databaseSlug, tableSlug, {});
      const isInvalid = isInvalidRow(invalid);
      runInAction(() => {
        if (this.table) {
          const cellStates = getCellsBaseState(content, invalid, isInvalid, this.table);
          this.tableState.push({ cellStates, isInvalid, entry: other });
        }
      });
    }
  }

  @action
  updateCellState(newCellState: CellStateEnum, location: CellLocation) {
    const { row, column } = location;
    const currentCell = this.tableState[row].cellStates[column];
    if (currentCell.state !== newCellState) {
      currentCell.state = newCellState;
    }
  }

  @action
  async updateCellValue(
    databaseSlug: string,
    tableSlug: string,
    newCellValue: EntryValueType, 
    location: CellLocation,
  ) {
    const { row, column } = location;
    const { cellStates, entry } = this.tableState[row];
    const currentCell = cellStates[column];

    const fieldNames = this.table?.keyOrder?.length
      ? this.table?.keyOrder
      : Object.keys(this.table?.schema || {});

    // convert reference cells back to to their UUID form
    const currentContent: EntryContent = cellStates.reduce((acc, cellState, index) => {
      const fieldName = fieldNames[index];
      if (cellState.value !== undefined) {
        return { ...acc, [fieldName]: entryToUuidForm(cellState.value) };
      }
      return acc;
    }, {});

    if (
      (isInvalidValue(currentCell.value) && isInvalidValue(newCellValue)) ||
      currentCell.value === newCellValue
    ) {
      return;
    }

    const fieldName = fieldNames[column];
    const fieldValue = entryToUuidForm(newCellValue);
    const fieldType = this.table?.schema[fieldName].type;

    let response = await updateEntry(databaseSlug, tableSlug, entry.uuid, {
      ...currentContent,
      [fieldName]: fieldValue,
    });

    // The updateEntry API does not return resolved references, as the getTableEntries API does.
    // Since the cell expects a reference to be resolved, we need to append the original reference
    // value (which is resolved) to the response before updating the cell value.
    const isReferenceField = fieldType && ['multi-reference', 'single-reference'].includes(fieldType);
    if (isReferenceField) {
      response = {
        ...response,
        content: response.content[fieldName] ? {
          ...response.content,
          [fieldName]: newCellValue,
        } : response.content,
      };
    }
    runInAction(() => this.updateCell(location, fieldName, response));
  }

  @action
  async uploadAsset(
    databaseSlug: string, tableSlug: string, file: File, location: CellLocation, assetType: MediaUploadType,
  ) {
    const { row, column } = location;
    const { entry } = this.tableState[row];
    
    const fieldNames = this.table?.keyOrder?.length ? this.table?.keyOrder : Object.keys(this.table?.schema || {});
    const fieldName = fieldNames[column];
    const response = await getFileUploaded(assetType, file, {
      databaseSlug,
      tableSlug,
      uuid: entry.uuid,
      fieldName,
    });
    runInAction(() => this.updateCell(location, fieldName, response));
  }

  @action
  updateCell(location: CellLocation, fieldName: string, updatedEntry: Entry) {
    const { row, column } = location;
    const { content, invalid, updatedAt } = updatedEntry;
    const { cellStates, entry } = this.tableState[row];
  
    // Update entry's updatedAt date and isInvalid flag; update cell's state, value,
    // and invalid
    entry.updatedAt = updatedAt;
    const currentCell = cellStates[column];
    currentCell.value = content[fieldName];
    currentCell.invalid = invalid?.[fieldName];
    const isInvalid = isInvalidRow(invalid);
    this.tableState[row].isInvalid = isInvalid;
    this.updateRowState(isInvalid ? 'Disabled' : 'Default', row);
    if (currentCell.invalid) {
      currentCell.state = 'Error';
    }
  }

  @action
  updateRowState(newCellState: CellStateEnum, row: number) {
    this.tableState[row].cellStates.forEach((_, index) => this.updateCellState(newCellState, { row, column: index }));
  }

  @action
  resetRowState(row: number) {
    const { cellStates, isInvalid } = this.tableState[row];
    cellStates.forEach(({ invalid }, index) => {
      const newCellState = getBaseCellState(!!invalid, isInvalid);
      this.updateCellState(newCellState, { row, column: index });
    });
  }

  @action
  setSelectedRow(row: number | undefined) {
    this.selectedRow = row;
  }

  @action
  setHoveredRow(row: number | undefined) {
    this.hoveredRow = row;
  }

  @action
  toggleModal() {
    this.isModalOpen = !this.isModalOpen;
  }

  @action
  resetStore() {
    this.table = undefined;
    this.tableState = [];
    this.selectedRow = undefined;
    this.hoveredRow = undefined;
    this.isModalOpen = false;
  }
}

export default TableStore;
